<template>
  <div>
    <!-- Mobile Number Header -->
    <div class="d-flex justify-content-between align-items-center mb-1">
      <span class="text-muted font-small-3">
        Mobile: <strong>{{ mobileNumber }}</strong>
      </span>
    </div>

    <!-- Date Filter Row -->
    <b-row class="mb-1">
      <b-col cols="12" sm="2">
        <b-form-group label="From Date" label-class="font-small-4" class="mb-0">
          <flat-pickr
            v-model="filter.fromDate"
            :config="dateConfig"
            class="form-control form-control-sm"
            placeholder="From Date"
          />
        </b-form-group>
      </b-col>

      <b-col cols="12" sm="2">
        <b-form-group label="To Date" label-class="font-small-4" class="mb-0">
          <flat-pickr
            v-model="filter.toDate"
            :config="dateConfig"
            class="form-control form-control-sm"
            placeholder="To Date"
          />
        </b-form-group>
      </b-col>

      <b-col
        cols="12"
        sm="4"
        class="d-flex align-items-end justify-content-start mt-1 mt-sm-0"
      >
        <b-button
          variant="primary"
          size="sm"
          class="mr-1"
          :disabled="loading"
          @click="applyFilter"
        >
          <feather-icon icon="SearchIcon" size="14" class="mr-25" />
          Filter
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="loading"
          @click="clearFilter"
        >
          <feather-icon icon="XIcon" size="14" class="mr-25" />
          Clear
        </b-button>
      </b-col>
    </b-row>

    <div class="d-flex align-items-center justify-content-between mb-1">
        <!-- Left side -->
        <div class="d-flex align-items-center">
            <label class="mr-2 mb-0 font-small-3">Per Page</label>

            <b-form-select
            v-model="perPage"
            :options="perPageOptions"
            size="sm"
            class="w-auto"
            @change="onPerPageChange"
            />

            <label class="mb-0 font-small-3 ml-1">
            Total {{ totalRecords }} entries
            </label>
        </div>

        <!-- Right side (Excel Button) -->
        <div v-if="transactions.length">
            <b-button
            variant="success"
            size="sm"
            @click="downloadCSV"
            >
            <feather-icon icon="DownloadIcon" size="14" class="mr-25" />
            Export Excel
            </b-button>
        </div>
    </div>

    <!-- Table -->
    <b-table
      responsive
      :items="paginatedTransactions"
      :fields="tableColumns"
      show-empty
      empty-text="No transactions found"
      :busy="loading"
      small
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle mr-1" small />
          <strong>Loading...</strong>
        </div>
      </template>

      <!-- SL -->
      <template #cell(sl)="data">
        {{ (currentPage - 1) * perPage + data.index + 1 }}
      </template>

      <!-- Transaction ID -->
      <template #cell(transactionID)="data">
        <span class="font-weight-bold text-primary">
          {{ data.item.transactionID || "—" }}
        </span>
      </template>

      <!-- Date & Time -->
      <template #cell(time)="data">
        {{ formatDateTime(data.item.time) }}
      </template>

      <!-- Amount -->
      <template #cell(amount)="data">
        <b-badge :variant="data.item.amount >= 0 ? 'light-success' : 'light-danger'">
         {{ data.item.amount }}
        </b-badge>
      </template>

      <!-- Gateway Type -->
      <template #cell(gatewayType)="data">
        <b-badge variant="light-info" style="white-space: normal; text-align: left;">
          {{ data.item.gatewayType || "—" }}
        </b-badge>
      </template>

      <!-- Added By -->
      <template #cell(addedBy)="data">
        {{ data.item.addedBy || "—" }}
      </template>

      <!-- Description -->
      <template #cell(description)="data">
    {{ data.item.description || "—" }}
      </template>
    </b-table>

    <!-- Pagination -->
    <b-row class="mt-1">
      <b-col
        cols="12"
        sm="5"
        class="d-flex align-items-center justify-content-center justify-content-sm-start"
      >
        <span class="text-muted font-small-3">
          Showing {{ fromEntry }} to {{ toEntry }} of {{ totalRecords }} entries
        </span>
      </b-col>
      <b-col
        cols="12"
        sm="7"
        class="d-flex align-items-center justify-content-center justify-content-sm-end"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRecords"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0 mt-1 mt-sm-0"
          prev-class="prev-item"
          next-class="next-item"
          size="sm"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="16" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="16" />
          </template>
        </b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BTable,
  BBadge,
  BPagination,
  BRow,
  BCol,
  BFormSelect,
  BFormGroup,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import api from "@/services/api";

export default {
  name: "KothonTransaction",

  components: {
    BTable,
    BBadge,
    BPagination,
    BRow,
    BCol,
    BFormSelect,
    BFormGroup,
    BButton,
    BSpinner,
    flatPickr,
  },

  props: {
    mobileNumber: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      transactions: [],
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 20, 50],

      // UI-bound filter fields (only committed to API on Filter click)
      filter: {
        fromDate: null,
        toDate: null,
      },

      // Committed filter — what's actually sent to the API
      activeFilter: {
        fromDate: null,
        toDate: null,
      },

      dateConfig: {
        altFormat: "d/m/Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },

      tableColumns: [
        { key: "sl",            label: "#",              sortable: false },
        { key: "amount",        label: "Amount",         sortable: false },
        { key: "gatewayType",   label: "Gateway Type",   sortable: false },
        { key: "addedBy",       label: "Added By",       sortable: false },
        { key: "description",   label: "Description",    sortable: false },
        { key: "transactionID", label: "Transaction ID", sortable: false },
        { key: "time",          label: "Date & Time",    sortable: false },
      ],
    };
  },

  computed: {
    totalRecords() {
      return this.transactions.length;
    },

    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.transactions.slice(start, start + this.perPage);
    },

    fromEntry() {
      if (!this.totalRecords) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },

    toEntry() {
      return Math.min(this.currentPage * this.perPage, this.totalRecords);
    },
  },

  watch: {
    // Re-fetch when parent opens modal with a different mobile number
    mobileNumber(newVal) {
      if (newVal) {
        this.resetState();
        this.fetchTransactions();
      }
    },
  },

  mounted() {
    if (this.mobileNumber) {
    //   this.fetchTransactions();
    }
  },

  methods: {
    downloadCSV() {
    if (!this.transactions.length) return;

    // Header row
    const headers = [
      "SL",
      "Amount",
      "Gateway Type",
      "Added By",
      "Description",
      "Transaction ID",
      "Date & Time",
    ];

    // Data rows
    const rows = this.transactions.map((item, index) => [
      index + 1,
      item.amount,
      item.gatewayType || "-",
      item.addedBy || "-",
      item.description || "-",
      item.transactionID || "-",
      this.formatDateTime(item.time),
    ]);

    // Combine header + rows
    const csvContent = [headers, ...rows]
      .map(row =>
        row
          .map(value => `"${String(value).replace(/"/g, '""')}"`) // escape quotes
          .join(",")
      )
      .join("\n");

    // Create Blob
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    // Create download link
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Transactions_${this.mobileNumber}_${Date.now()}.csv`
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
    // ✅ Filter button: validate → commit → API call
    applyFilter() {
      if (!this.filter.fromDate || !this.filter.toDate) {
        this.$bvToast.toast("Please select both From Date and To Date.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return;
      }

      // Commit UI filter to active filter
      this.activeFilter.fromDate = this.filter.fromDate;
      this.activeFilter.toDate   = this.filter.toDate;
      this.currentPage           = 1;
      this.fetchTransactions();
    },

    // ✅ Clear button: wipe all filter state → re-fetch without filters
    clearFilter() {
      this.filter.fromDate       = null;
      this.filter.toDate         = null;
      this.activeFilter.fromDate = null;
      this.activeFilter.toDate   = null;
      this.currentPage           = 1;
      this.transactions            = [];
    //   this.fetchTransactions();
    },

    // ✅ Per-page dropdown change → reset page → re-fetch (triggers new slice)
    onPerPageChange() {
      this.currentPage = 1;
      this.fetchTransactions();
    },

    // ✅ Core API call — always uses activeFilter (not UI filter)
    async fetchTransactions() {
      this.loading      = true;
      this.transactions = [];

      try {
        const params = {
          mobile_number: this.mobileNumber,
        };

        // Only include date params if filter is active
        if (this.activeFilter.fromDate) params.from_date = this.activeFilter.fromDate;
        if (this.activeFilter.toDate)   params.to_date   = this.activeFilter.toDate;

        const res = await api.get("/kothon/api/v1/kothon_transactions", { params });

        this.transactions = Array.isArray(res.data) ? res.data : [];
      } catch (error) {
        console.error("Transaction fetch failed:", error);

        this.$bvToast.toast(
          error.response?.data?.message || "Failed to load transactions",
          {
            title: "Error",
            variant: "danger",
            solid: true,
          }
        );

        this.transactions = [];
      } finally {
        this.loading = false;
      }
    },

    // ✅ Full state reset when mobile number changes via parent
    resetState() {
      this.transactions          = [];
      this.currentPage           = 1;
      this.filter.fromDate       = null;
      this.filter.toDate         = null;
      this.activeFilter.fromDate = null;
      this.activeFilter.toDate   = null;
    },

    formatDateTime(ms) {
      if (!ms) return "—";
      const d   = new Date(ms);
      const pad = (n) => String(n).padStart(2, "0");
      return (
        `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ` +
        `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
      );
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style scoped>
.text-truncate {
  vertical-align: middle;
}
</style>